import { useTranslation } from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import { useMutation } from "@outschool/ui-apollo";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAnalytics } from "use-analytics";

import {
  LearnerQuizStartQuizAttemptMutation,
  LearnerQuizStartQuizAttemptMutationVariables,
} from "../../generated/graphql";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import { useLearnerAuth } from "../../providers/LearnerAuthProvider";
import { QUIZ_GENERATION_PROGRESS_CAP, QUIZ_WRITER_WS_PATH } from "./consts";
import { LearnerQuizStartQuizAttempt } from "./quizQueries";
import { QuizQuestion } from "./types";

export type QuizGenerateLaunchOptions = {
  topicUid: string;
  topicName?: string;
};

interface UseQuizGeneratorWebsocketOptions {
  topicUid: string;
  topicDisplayName: string;
  topicSuggestions: Array<{ uid: string; name: string }>;
  setTopicUid: React.Dispatch<React.SetStateAction<string>>;
  setTopicNameOverride: React.Dispatch<React.SetStateAction<string>>;
  setQuestions: React.Dispatch<React.SetStateAction<QuizQuestion[]>>;
  setSelectedAnswers: React.Dispatch<
    React.SetStateAction<Record<number, number>>
  >;
  setActiveQuestionIndex: React.Dispatch<React.SetStateAction<number>>;
  setRevealedByIndex: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  setAnsweredCorrectByIndex: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  resetTopicGeneration: () => void;
  refreshDidYouKnowFact: () => void;
  resetAttemptCompletion: () => void;
}

export function useQuizGeneratorWebsocket({
  topicUid,
  topicDisplayName,
  topicSuggestions,
  setTopicUid,
  setTopicNameOverride,
  setQuestions,
  setSelectedAnswers,
  setActiveQuestionIndex,
  setRevealedByIndex,
  setAnsweredCorrectByIndex,
  resetTopicGeneration,
  refreshDidYouKnowFact,
  resetAttemptCompletion,
}: UseQuizGeneratorWebsocketOptions) {
  const { t } = useTranslation("learnerApp\\Quiz");
  const { track } = useAnalytics();
  const { sessionTokenStore } = useLearnerAuth();
  const currentLearner = useCurrentLearner();
  const [startQuizAttemptMutation] = useMutation<
    LearnerQuizStartQuizAttemptMutation,
    LearnerQuizStartQuizAttemptMutationVariables
  >(LearnerQuizStartQuizAttempt);

  const [isQuizGenerating, setIsQuizGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generationLogStepCount, setGenerationLogStepCount] = useState(0);
  const [latestUserLogMessage, setLatestUserLogMessage] = useState<
    string | null
  >(null);
  const [attemptUid, setAttemptUid] = useState<string | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const quizGenerationGotResultRef = useRef(false);

  const closeQuizGeneratorWebsocket = useCallback(() => {
    socketRef.current?.close();
    socketRef.current = null;
  }, []);

  const startQuizGeneratorWebsocket = useCallback(
    (launch?: QuizGenerateLaunchOptions) => {
      const resolvedTopicUid = launch?.topicUid ?? topicUid;
      const resolvedDisplayName =
        launch?.topicName ??
        (launch?.topicUid
          ? topicSuggestions.find(x => x.uid === launch.topicUid)?.name ?? ""
          : topicDisplayName);

      if (!resolvedTopicUid || isQuizGenerating) {
        return;
      }

      if (launch?.topicUid) {
        setTopicUid(launch.topicUid);
        setTopicNameOverride(launch.topicName ?? "");
      }

      track("quiz_create_button_click", {
        topic: resolvedDisplayName,
        topicUid: resolvedTopicUid,
      });

      const token = sessionTokenStore?.getSessionToken();
      if (!token) {
        setError(t("Please sign in to generate a quiz."));
        return;
      }

      socketRef.current?.close();
      resetTopicGeneration();
      setError(null);
      setQuestions([]);
      setSelectedAnswers({});
      setActiveQuestionIndex(0);
      setRevealedByIndex({});
      setAnsweredCorrectByIndex({});
      setGenerationLogStepCount(0);
      setLatestUserLogMessage(null);
      refreshDidYouKnowFact();
      quizGenerationGotResultRef.current = false;
      resetAttemptCompletion();
      setAttemptUid(null);
      setIsQuizGenerating(true);

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(
        `${protocol}//${window.location.host}${QUIZ_WRITER_WS_PATH}`,
      );
      socketRef.current = ws;

      let quizPayloadSent = false;

      const sendQuizPayload = () => {
        if (quizPayloadSent || ws.readyState !== WebSocket.OPEN) {
          return;
        }
        quizPayloadSent = true;
        ws.send(
          JSON.stringify({
            topicUid: resolvedTopicUid,
            age: currentLearner?.age,
          }),
        );
      };

      ws.onopen = () => {
        ws.send(JSON.stringify({ token }));
      };

      ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data as string) as {
            type?: string;
            message?: string;
            questions?: QuizQuestion[];
            quizUid?: string;
          };
          switch (data.type) {
            case "ready":
              sendQuizPayload();
              break;
            case "user_log":
              if (typeof data.message === "string" && data.message.length > 0) {
                setLatestUserLogMessage(data.message);
                setGenerationLogStepCount(prev =>
                  Math.min(prev + 1, QUIZ_GENERATION_PROGRESS_CAP),
                );
              }
              break;
            case "result": {
              quizGenerationGotResultRef.current = true;
              const quizUidFromResult =
                typeof data.quizUid === "string" && data.quizUid.length > 0
                  ? data.quizUid
                  : null;
              if (Array.isArray(data.questions)) {
                setQuestions(data.questions);
                track("quiz_generation_success", {
                  topic: resolvedDisplayName,
                  topicUid: resolvedTopicUid,
                });
              }
              resetAttemptCompletion();
              void (async () => {
                if (quizUidFromResult) {
                  try {
                    const result = await startQuizAttemptMutation({
                      variables: { quizUid: quizUidFromResult },
                    });
                    const uid = result.data?.startQuizAttempt?.uid;
                    if (uid) {
                      setAttemptUid(uid);
                    }
                  } catch (error) {
                    OsPlatform.captureError(error, {
                      component: Component.QuizWriter,
                    });
                  }
                } else {
                  setAttemptUid(null);
                }
                setIsQuizGenerating(false);
                socketRef.current = null;
              })();
              break;
            }
            case "error":
              setError(data.message ?? t("Something went wrong."));
              setIsQuizGenerating(false);
              socketRef.current = null;
              break;
            default:
              break;
          }
        } catch (error) {
          OsPlatform.captureError(error, {
            component: Component.QuizWriter,
          });
        }
      };

      ws.onclose = () => {
        if (socketRef.current !== ws) {
          return;
        }
        socketRef.current = null;
        setIsQuizGenerating(prevLoading => {
          if (prevLoading && !quizGenerationGotResultRef.current) {
            setError(t("Connection closed before the quiz finished."));
          }
          return false;
        });
      };

      ws.onerror = () => {
        setError(t("WebSocket connection error."));
        setIsQuizGenerating(false);
        socketRef.current = null;
      };
    },
    [
      currentLearner?.age,
      isQuizGenerating,
      refreshDidYouKnowFact,
      resetAttemptCompletion,
      resetTopicGeneration,
      sessionTokenStore,
      setActiveQuestionIndex,
      setAnsweredCorrectByIndex,
      setQuestions,
      setRevealedByIndex,
      setSelectedAnswers,
      setTopicNameOverride,
      setTopicUid,
      startQuizAttemptMutation,
      t,
      topicDisplayName,
      topicSuggestions,
      topicUid,
      track,
    ],
  );

  useEffect(() => {
    return () => {
      closeQuizGeneratorWebsocket();
    };
  }, [closeQuizGeneratorWebsocket]);

  return {
    attemptUid,
    setAttemptUid,
    isQuizGenerating,
    setIsQuizGenerating,
    error,
    setError,
    generationLogStepCount,
    latestUserLogMessage,
    startQuizGeneratorWebsocket,
    closeQuizGeneratorWebsocket,
  };
}
